import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { sendMessage } from '../../../chat/actions.any';
import { feedbackFailed, feedbackRequested, openSlidePanel } from '../../../slide-view/actions';
import { sendConversationFeedback } from '../../actions';
import { getAiInterims, getInterimTranscripts, getTranscripts, isChatHistoryVisible } from '../../functions';
import './chat-history-panel.css';
/**
 * Formats an epoch-ms timestamp as HH:MM.
 *
 * @param {number} ts - Milliseconds since epoch.
 * @returns {string}
 */
function _time(ts) {
    const d = new Date(ts);
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
}
/**
 * Right-column live conversation log: accumulated AI speech-to-text (final)
 * plus a per-speaker in-progress line.
 *
 * @param {IProps} props - Component props.
 * @returns {ReactElement|null}
 */
const ChatHistoryPanel = ({ _aiInterims, _feedbackRunning, _feedbackUnread, _interims, _transcripts, _visible }) => {
    const endRef = useRef(null);
    const [filter, setFilter] = useState('all');
    const [input, setInput] = useState('');
    const dispatch = useDispatch();
    // AI評価レポート: 蓄積済みの会話履歴をAIへ送って評価PDFを作らせる。
    // レポートはスライドパネル (PDF_SLIDE_URL) で届く。
    // ★2026-10-12 押した瞬間に進行ポップアップ（FeedbackProgressModal）を出し、
    //   届くまでボタンは「作成中」で止める。未読のレポートがあるときはボタンで開く。
    const onFeedback = useCallback(() => {
        if (_feedbackUnread) {
            dispatch(openSlidePanel());
            return;
        }
        const sent = dispatch(sendConversationFeedback());
        dispatch(sent
            ? feedbackRequested()
            : feedbackFailed('会話がまだ無いか、AIが参加していません。少し話してからもう一度お試しください。'));
    }, [dispatch, _feedbackUnread]);
    const fbSent = _feedbackRunning;
    // Send the typed text through the same path as the (removed) chat button.
    // The text is processed as STT upstream and echoed back over the data
    // channel, so we do NOT add it to the log locally.
    const onSend = useCallback((e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text) {
            return;
        }
        dispatch(sendMessage(text));
        setInput('');
    }, [input, dispatch]);
    // Keep the newest line in view as finals accumulate and interims update.
    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [
        _transcripts.length,
        _interims.map(i => i.text).join('|'),
        _aiInterims.map(i => i.text).join('|')
    ]);
    if (!_visible) {
        return null;
    }
    // Apply the すべて / ユーザー / AI filter.
    const showAi = filter !== 'user';
    const showUser = filter !== 'ai';
    const transcripts = _transcripts.filter(l => (l.isAi ? showAi : showUser));
    const interims = showUser ? _interims : [];
    const aiInterims = showAi ? _aiInterims : [];
    const isEmpty = transcripts.length === 0 && interims.length === 0 && aiInterims.length === 0;
    // Group consecutive lines from the same speaker (AI, or a specific user)
    // into one block so a chatty AI reads as a single lane, not repeated headers.
    const groups = [];
    for (const line of transcripts) {
        const key = line.isAi ? 'ai' : `u:${line.participantId}`;
        const last = groups[groups.length - 1];
        if (last && last.key === key) {
            last.lines.push(line);
        }
        else {
            groups.push({ isAi: Boolean(line.isAi), key, lines: [line], name: line.name, ts: line.ts });
        }
    }
    return (React.createElement("div", { className: `chat-history-panel ${_visible ? 'visible' : ''}` },
        React.createElement("div", { className: 'chat-history-topbar' },
            React.createElement("div", { className: 'chat-history-header' },
                React.createElement("h2", { className: 'chat-history-title' }, "\u4F1A\u8A71\u5C65\u6B74"),
                React.createElement("button", { className: 'chf-feedback', disabled: fbSent, onClick: onFeedback, title: 'AI\u304C\u4F1A\u8A71\u3092\u8A55\u4FA1\u3057\u3066\u30EC\u30DD\u30FC\u30C8\u3092\u4F5C\u6210\u3057\u307E\u3059', type: 'button' },
                    React.createElement("span", { className: 'chf-feedback-icon' }, fbSent ? '⏳' : '📊'),
                    React.createElement("span", { className: 'chf-feedback-label' }, fbSent ? 'レポート作成中…' : '会話を評価'),
                    _feedbackUnread && React.createElement("span", { className: 'chf-feedback-badge' }))),
            React.createElement("div", { className: 'chat-history-subbar' },
                React.createElement("div", { className: 'chat-history-filter' }, [['all', 'すべて'], ['user', 'ユーザー'], ['ai', 'AI']].map(([f, label]) => (React.createElement("button", { className: `chf-btn chf-${f} ${filter === f ? 'on' : ''}`, key: f, onClick: () => setFilter(f), type: 'button' }, label)))))),
        React.createElement("div", { className: 'chat-history-content' },
            isEmpty && (React.createElement("div", { className: 'chat-history-empty' }, "\u307E\u3060\u767A\u8A71\u304C\u3042\u308A\u307E\u305B\u3093")),
            groups.map(g => (React.createElement("div", { className: `stt-group ${g.isAi ? 'stt-group--ai' : 'stt-group--user'}`, key: `${g.key}-${g.lines[0].id}` },
                React.createElement("div", { className: 'stt-group__head' },
                    React.createElement("span", { className: 'stt-group__name' }, g.isAi ? '🤖 AI' : g.name),
                    React.createElement("span", { className: 'stt-group__time' }, _time(g.ts))),
                g.lines.map(l => (React.createElement("div", { className: 'stt-group__line', key: l.id }, l.text)))))),
            interims.map(line => (React.createElement("div", { className: 'stt-group stt-group--user stt-group--live', key: `interim-${line.participantId}` },
                React.createElement("div", { className: 'stt-group__head' },
                    React.createElement("span", { className: 'stt-group__name' }, line.name),
                    React.createElement("span", { className: 'stt-live-badge' }, "\u25CF \u8A8D\u8B58\u4E2D")),
                React.createElement("div", { className: 'stt-group__line' },
                    line.text,
                    React.createElement("span", { className: 'stt-caret' }, "\u258D"))))),
            aiInterims.map(line => (React.createElement("div", { className: 'stt-group stt-group--ai stt-group--live', key: `ai-interim-${line.id}` },
                React.createElement("div", { className: 'stt-group__head' },
                    React.createElement("span", { className: 'stt-group__name' }, "\uD83E\uDD16 AI"),
                    React.createElement("span", { className: 'stt-live-badge' }, "\u25CF \u751F\u6210\u4E2D")),
                React.createElement("div", { className: 'stt-group__line' },
                    line.text,
                    React.createElement("span", { className: 'stt-caret' }, "\u258D"))))),
            React.createElement("div", { ref: endRef })),
        React.createElement("form", { className: 'chat-history-input', onSubmit: onSend },
            React.createElement("input", { "aria-label": '\u30E1\u30C3\u30BB\u30FC\u30B8\u3092\u5165\u529B', onChange: e => setInput(e.target.value), placeholder: '\u30E1\u30C3\u30BB\u30FC\u30B8\u3092\u5165\u529B\u2026', type: 'text', value: input }),
            React.createElement("button", { "aria-label": '\u9001\u4FE1', disabled: !input.trim(), type: 'submit' }, "\u27A4"))));
};
/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code ChatHistoryPanel} component.
 *
 * @param {IReduxState} state - The Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    return {
        _aiInterims: getAiInterims(state),
        _feedbackRunning: state['features/slide-view']?.feedback?.phase === 'running',
        _feedbackUnread: Boolean(state['features/slide-view']?.feedbackUnread),
        _interims: getInterimTranscripts(state),
        _transcripts: getTranscripts(state),
        _visible: isChatHistoryVisible(state)
    };
}
export default connect(mapStateToProps)(ChatHistoryPanel);
